// src/components/common/FloatingAccessButton.tsx
// Standalone floating action button component
// No dependencies on Buttons.tsx or MediaQueryUtils
// Simple, flat props API for customization

import React, { useState } from 'react';
import { Box } from '@mui/material';

interface FloatingAccessButtonProps {
  // Core functionality
  icon: React.ReactNode;
  onClick: () => void;

  // Accessibility
  'aria-label'?: string;
  ariaLabel?: string; // Alternative prop name

  // Icon variants on hover
  hoverIcon?: React.ReactNode;

  // Icon scaling
  iconScale?: number;

  // Visual styling - flat props
  backgroundColor?: string;
  color?: string;
  width?: string | number;
  height?: string | number;
  borderRadius?: string;
  elevation?: number;
  iconSize?: string | number;

  // Hover state variants
  hoverBackgroundColor?: string;
  hoverColor?: string;
  hoverElevation?: number;
  hoverIconSize?: string | number;
  hoverScale?: number;

  // Positioning
  position?: 'fixed' | 'absolute' | 'relative';
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;

  // Advanced
  disabled?: boolean;
  sx?: any;
  className?: string;
}

// Standalone rounded floating button with icon. Self-contained, no
// dependencies on styled() or MediaQueryUtils; all styling goes through sx.
export const FloatingAccessButton = React.forwardRef<
  HTMLDivElement,
  FloatingAccessButtonProps
>(
  (
    {
      icon,
      onClick,
      'aria-label': ariaLabelProp,
      ariaLabel,
      hoverIcon,
      iconScale = 1,
      backgroundColor = '#2196F3',
      color = '#ffffff',
      width = 56,
      height = 56,
      borderRadius = '50%',
      elevation = 6,
      iconSize = 24,
      hoverBackgroundColor,
      hoverColor,
      hoverElevation,
      hoverIconSize,
      hoverScale = 1.1,
      position = 'fixed',
      top,
      bottom = '2rem',
      left,
      right = '2rem',
      disabled = false,
      sx,
      className,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    // Helper: convert number to px string
    const toPx = (val: string | number | undefined): string | undefined => {
      if (val === undefined || val === null) return undefined;
      return typeof val === 'number' ? `${val}px` : val;
    };

    // Compute Material Design elevation shadow
    const getElevationShadow = (elevation: number): string => {
      const shadowMap: Record<number, string> = {
        0: 'none',
        1: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
        2: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
        6: '0px 6px 6px -3px rgba(0,0,0,0.2), 0px 10px 14px 1px rgba(0,0,0,0.14), 0px 4px 5px 0px rgba(0,0,0,0.12)',
        12: '0px 12px 17px -5px rgba(0,0,0,0.2), 0px 19px 29px 2px rgba(0,0,0,0.14), 0px 9px 9px 0px rgba(0,0,0,0.12)',
        24: '0px 24px 37px -5px rgba(0,0,0,0.2), 0px 35px 55px 2px rgba(0,0,0,0.14), 0px 13px 13px 0px rgba(0,0,0,0.12)',
      };
      return shadowMap[elevation] || shadowMap[6];
    };

    const boxShadow = getElevationShadow(elevation);
    const hoverBoxShadow =
      hoverElevation !== undefined
        ? getElevationShadow(hoverElevation)
        : boxShadow;

    // Resolve aria-label from either prop name
    const resolvedAriaLabel =
      ariaLabelProp || ariaLabel || 'Floating action button';

    // Determine which icon to display
    const displayIcon = isHovered && hoverIcon ? hoverIcon : icon;

    // Build sx object with all computed styles
    const computedSx: any = {
      // Positioning
      position,
      top: toPx(top),
      bottom: toPx(bottom),
      left: toPx(left),
      right: toPx(right),

      // Sizing
      width: toPx(width),
      height: toPx(height),
      minWidth: toPx(width),
      minHeight: toPx(height),

      // Styling
      borderRadius,
      backgroundColor,
      color,
      boxShadow,

      // Layout
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      // Interaction
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      userSelect: 'none',

      // Transitions
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 1000,

      // Icon styling
      '& svg': {
        fontSize: toPx(iconSize),
        width: toPx(iconSize),
        height: toPx(iconSize),
        transform: `scale(${iconScale})`,
        transition: 'font-size 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      },

      // Hover state
      '&:hover': {
        backgroundColor: hoverBackgroundColor || backgroundColor,
        color: hoverColor || color,
        boxShadow: hoverBoxShadow,
        transform: `scale(${hoverScale})`,

        '& svg': {
          fontSize: toPx(hoverIconSize || iconSize),
          width: toPx(hoverIconSize || iconSize),
          height: toPx(hoverIconSize || iconSize),
        },
      },

      // Active state
      '&:active': {
        transform: 'scale(0.95)',
      },

      // Focus state (keyboard navigation)
      '&:focus-visible': {
        outline: `3px solid ${backgroundColor}`,
        outlineOffset: '2px',
      },

      // Disabled state
      ...(disabled && {
        cursor: 'not-allowed',
        opacity: 0.6,
        pointerEvents: 'none',
      }),

      // User overrides
      ...sx,
    };

    return (
      <Box
        ref={ref}
        onClick={!disabled ? onClick : undefined}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={resolvedAriaLabel}
        aria-disabled={disabled}
        className={className}
        sx={computedSx}
      >
        {displayIcon}
      </Box>
    );
  }
);

FloatingAccessButton.displayName = 'FloatingAccessButton';

export default FloatingAccessButton;
